import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const InterviewPrepPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [selectedFaq, setSelectedFaq] = useState(null);

  // Toggle FAQ visibility
  const toggleFaq = (index) => {
    if (selectedFaq === index) {
      setSelectedFaq(null);
    } else {
      setSelectedFaq(index);
    }
  };

  // Mock data for interview tips
  const interviewTips = [
    {
      id: 1,
      title: "Know Your DAF Inside Out",
      description:
        "Your Detailed Application Form (DAF) is the foundation of your interview. Every detail you've mentioned can become a potential question area.",
      tips: [
        "Review your form multiple times and be ready to elaborate on every point",
        "For work experience, be prepared to discuss your responsibilities and achievements",
        "For hobbies and interests, have meaningful insights to share beyond surface-level knowledge",
      ],
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      ),
    },
    {
      id: 2,
      title: "Stay Updated on Current Affairs",
      description:
        "The NPSC interview often includes questions on current events, especially those relevant to Nagaland.",
      tips: [
        "Follow national and local news regularly",
        "Focus on Nagaland-specific issues, governance, and developments",
        "Develop informed opinions on controversial topics, but present balanced views",
      ],
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v10m2 2v-6a2 2 0 00-2-2h-6m6 8l-4-4 4-4m-8 4h6"
          />
        </svg>
      ),
    },
    {
      id: 3,
      title: "Practice Effective Communication",
      description:
        "Your ability to articulate ideas clearly and concisely is crucial for success in the NPSC interview.",
      tips: [
        "Structure your answers with a clear beginning, middle, and conclusion",
        "Use simple language and avoid jargon unless specifically asked",
        "Practice the optimal answer length (typically 1-2 minutes per question)",
      ],
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
          />
        </svg>
      ),
    },
    {
      id: 4,
      title: "Showcase Your Personality",
      description:
        "The interview evaluates not just your knowledge but your character, attitude, and suitability for public service.",
      tips: [
        "Demonstrate qualities like integrity, empathy, and leadership through your answers",
        "Be authentic rather than presenting a manufactured persona",
        "Show enthusiasm for public service and contributing to Nagaland's development",
      ],
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
          />
        </svg>
      ),
    },
    {
      id: 5,
      title: "Master Your Body Language",
      description:
        "Non-verbal communication speaks volumes during your interview and contributes significantly to the impression you make.",
      tips: [
        "Maintain good posture and appropriate eye contact",
        "Use natural hand gestures to emphasize points, but avoid excessive movements",
        "Practice a confident, pleasant facial expression even when facing difficult questions",
      ],
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
    {
      id: 6,
      title: "Develop Ethical Reasoning",
      description:
        "Questions on ethics and integrity are common in NPSC interviews to assess your character and decision-making abilities.",
      tips: [
        "Study ethical frameworks and moral principles applicable to public service",
        "Practice analyzing ethical dilemmas from multiple perspectives",
        "Be prepared to justify your stance with logical reasoning",
      ],
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
          />
        </svg>
      ),
    },
  ];

  // Mock data for interview packages
  const interviewPackages = [
    {
      id: 1,
      title: "Basic Interview Preparation",
      description:
        "Essential preparation for NPSC personality test with core modules and practice sessions.",
      features: [
        "1 Mock Interview Session",
        "Basic Feedback Report",
        "Common Questions Preparation",
        "2 Days of Personalized Guidance",
        "DAF Analysis",
      ],
      price: "₹4,999",
      recommended: false,
    },
    {
      id: 2,
      title: "Comprehensive Interview Mastery",
      description:
        "Complete preparation package with extensive practice, detailed feedback, and continuous mentoring.",
      features: [
        "3 Mock Interview Sessions",
        "Detailed Feedback Reports",
        "Personalized Question Bank",
        "7 Days of Personalized Guidance",
        "DAF Analysis with Expert Insights",
        "Body Language Training",
        "Stress Interview Practice",
      ],
      price: "₹12,999",
      recommended: true,
    },
    {
      id: 3,
      title: "Elite Interview Excellence",
      description:
        "Premium preparation with former NPSC board members, tailored strategy, and intensive coaching.",
      features: [
        "5 Mock Interview Sessions",
        "Comprehensive Feedback Analysis",
        "Customized Question Bank",
        "14 Days of Personalized Guidance",
        "Expert DAF Review and Strategy",
        "Advanced Communication Training",
        "Stress & Panel Interview Simulation",
        "Post-Interview Counseling",
      ],
      price: "₹24,999",
      recommended: false,
    },
  ];

  // Mock data for sample questions
  const sampleQuestions = [
    {
      category: "Personal Background",
      questions: [
        "Tell us about yourself and your background.",
        "What motivated you to pursue a career in civil services?",
        "How has your education prepared you for a career in civil services?",
        "What are your strengths and weaknesses, and how do they relate to the position you've applied for?",
      ],
    },
    {
      category: "Nagaland Knowledge",
      questions: [
        "What do you consider the three most pressing challenges facing Nagaland today?",
        "How would you contribute to addressing the development needs of remote areas in Nagaland?",
        "What are your views on preserving Naga cultural heritage while embracing modernization?",
        "How would you promote tourism in Nagaland while respecting local traditions?",
      ],
    },
    {
      category: "Administrative Scenarios",
      questions: [
        "If posted as an SDO in a conflict-prone area, how would you handle local tensions?",
        "How would you improve service delivery in a department known for inefficiency?",
        "What steps would you take to promote transparency in your office?",
        "How would you handle a situation where you're pressured to make a decision that benefits a few at the expense of many?",
      ],
    },
    {
      category: "Current Affairs",
      questions: [
        "What are your views on the current peace process in Nagaland?",
        "How do you think the recent changes in [relevant policy] will affect Nagaland?",
        "What is your opinion on the role of social media in governance?",
        "How can Nagaland better integrate with the national economy while maintaining its unique identity?",
      ],
    },
    {
      category: "Ethics & Integrity",
      questions: [
        "What does integrity in public service mean to you?",
        "Describe a situation where you faced an ethical dilemma. How did you resolve it?",
        "How would you handle a conflict between your personal values and your official duties?",
        "What measures would you take to prevent corruption in your department?",
      ],
    },
  ];

  // Mock data for expert panel
  const expertPanel = [
    {
      id: 1,
      name: "Vizokho Zashumo",
      position: "Former NPSC Member",
      image: "/images/experts/vizokho-zashumo.jpg",
      experience: "15+ years in civil service and interview panels",
      expertise: "Interview techniques, personality assessment",
    },
    {
      id: 2,
      name: "Dr. Imtisunep Longchar",
      position: "Senior Faculty & NPSC Expert",
      image: "/images/experts/dr-longchar.jpg",
      experience: "Guided 150+ successful candidates",
      expertise: "Subject knowledge, answer structuring",
    },
    {
      id: 3,
      name: "Prof. Ketholenuo Mer",
      position: "Communication Specialist",
      image: "/images/experts/prof-mer.jpg",
      experience: "8+ years in communication training",
      expertise: "Non-verbal communication, public speaking",
    },
    {
      id: 4,
      name: "Dr. Rosemary Dzüvichü",
      position: "Current Affairs Expert",
      image: "/images/experts/dr-dzuvichu.jpg",
      experience: "10+ years in NPSC coaching",
      expertise: "Contemporary issues, opinion development",
    },
  ];

  // Mock data for testimonials
  const testimonials = [
    {
      id: 1,
      content:
        "The mock interviews were incredibly realistic and helped me identify weaknesses in my approach that I hadn't noticed before. The detailed feedback after each session was invaluable in improving my performance.",
      author: "Theja Kezo",
      position: "NPSC 2023 Selected Candidate",
      image: "/images/testimonials/testimonial-1.jpg",
    },
    {
      id: 2,
      content:
        "The interview preparation program completely transformed how I presented myself. From body language to answer structure, every aspect of my interview technique improved dramatically. I went into my NPSC interview with confidence.",
      author: "Alemla Imsong",
      position: "NPSC 2023 Selected Candidate",
      image: "/images/testimonials/testimonial-2.jpg",
    },
    {
      id: 3,
      content:
        "Having a former NPSC board member conduct my mock interviews gave me incredible insights into what the panel looks for. The personalized question bank prepared me for even the most challenging questions I faced.",
      author: "Khrienuo Metha",
      position: "NPSC 2022 Selected Candidate",
      image: "/images/testimonials/testimonial-3.jpg",
    },
  ];

  // Mock data for FAQs
  const faqs = [
    {
      question: "When should I start my interview preparation?",
      answer:
        "Ideally, you should begin your interview preparation as soon as you clear the Mains examination. This gives you enough time to address all aspects of the interview, from DAF analysis to communication skills improvement. However, intensive preparation should begin about 4-6 weeks before the expected interview date.",
    },
    {
      question:
        "How is the NPSC interview different from other competitive exams?",
      answer:
        "The NPSC interview has a stronger focus on Nagaland-specific issues, tribal dynamics, and local governance challenges. While it follows a similar pattern to other civil service interviews, there's greater emphasis on your understanding of Nagaland's unique cultural, social, and administrative context. The board also particularly evaluates your potential contribution to Nagaland's development.",
    },
    {
      question: "What is the format of the NPSC interview?",
      answer:
        "The NPSC interview typically lasts 20-30 minutes before a panel of 4-5 members. The chairperson usually begins with introductory questions based on your DAF, followed by other panel members asking questions related to your educational background, work experience, current affairs, and scenario-based administrative challenges. The interview evaluates your personality traits, knowledge, and suitability for civil service.",
    },
    {
      question: "How should I dress for the NPSC interview?",
      answer:
        "Dress formally but comfortably. For men, a light-colored shirt with dark trousers and a tie is appropriate. For women, a saree, salwar kameez, or formal western attire works well. Avoid flashy colors or heavy accessories. The focus should be on presenting a neat, professional appearance that allows the panel to concentrate on your answers rather than your attire.",
    },
    {
      question: "Can I say 'I don't know' if I'm unsure of an answer?",
      answer:
        "Yes, it's better to honestly admit when you don't know something rather than providing incorrect information or rambling. However, try to add something constructive, such as 'I'm not fully informed on this topic, but I believe the key considerations would include...' This shows intellectual honesty while still demonstrating your analytical abilities.",
    },
    {
      question:
        "How many mock interviews should I take before the actual interview?",
      answer:
        "Most successful candidates take at least 3-5 mock interviews with different panels to get varied feedback and experience different interviewing styles. This helps you become comfortable with the interview setting and improves your ability to handle unexpected questions. Our comprehensive packages include multiple mock sessions designed to progressively build your confidence and skills.",
    },
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gray-900 opacity-90"></div>
          <div className="absolute inset-0 bg-[url('https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRiqn5jXhC1CTq8isbIwTTte77VL0U7zhlBwQ&s')] bg-cover bg-center mix-blend-overlay"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-3xl">
            <motion.h1
              className="text-4xl md:text-5xl font-extrabold tracking-tight mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              NPSC Interview Preparation
            </motion.h1>
            <motion.p
              className="text-lg md:text-xl mb-8 text-gray-200"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              The interview is the final and often decisive stage of your NPSC
              journey. Our expert-led preparation program equips you with the
              skills, confidence, and strategy to excel in your personality
              test.
            </motion.p>
            <motion.div
              className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <a
                href="#packages"
                className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-gray-900 bg-yellow-400 hover:bg-yellow-500 transition-colors duration-300 shadow-md"
              >
                View Packages
              </a>
              <a
                href="#mock-interview"
                className="inline-flex items-center justify-center px-6 py-3 border-2 border-white text-base font-medium rounded-md text-white hover:bg-white/10 transition-colors duration-300"
              >
                Book Mock Interview
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Approach Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h2
              className="text-3xl font-extrabold text-gray-900 sm:text-4xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Our Interview Preparation Approach
            </motion.h2>
            <motion.p
              className="mt-4 max-w-2xl text-xl text-gray-600 mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              A comprehensive methodology developed by former NPSC board members
              and successful candidates
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-300"
            >
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 mb-4">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                1. Personalized Analysis
              </h3>
              <p className="text-gray-600 mb-4">
                We begin with a thorough analysis of your Detailed Application
                Form (DAF), educational background, and professional experience
                to identify potential question areas and develop a personalized
                preparation strategy.
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <svg
                    className="h-5 w-5 text-green-500 mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="ml-2">In-depth DAF review</span>
                </li>
                <li className="flex items-start">
                  <svg
                    className="h-5 w-5 text-green-500 mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="ml-2">Strength & weakness assessment</span>
                </li>
                <li className="flex items-start">
                  <svg
                    className="h-5 w-5 text-green-500 mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="ml-2">Customized preparation roadmap</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-300"
            >
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 mb-4">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                2. Comprehensive Practice
              </h3>
              <p className="text-gray-600 mb-4">
                Our realistic mock interviews simulate the actual NPSC interview
                environment, with panels comprising former board members and
                subject experts who provide constructive feedback.
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <svg
                    className="h-5 w-5 text-green-500 mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="ml-2">Realistic mock interviews</span>
                </li>
                <li className="flex items-start">
                  <svg
                    className="h-5 w-5 text-green-500 mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="ml-2">Expert panel evaluation</span>
                </li>
                <li className="flex items-start">
                  <svg
                    className="h-5 w-5 text-green-500 mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="ml-2">Video recording & analysis</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-300"
            >
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 mb-4">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                3. Targeted Improvement
              </h3>
              <p className="text-gray-600 mb-4">
                Based on feedback from mock interviews, we focus on specific
                areas for improvement through personalized coaching and targeted
                practice sessions.
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <svg
                    className="h-5 w-5 text-green-500 mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="ml-2">Communication skills enhancement</span>
                </li>
                <li className="flex items-start">
                  <svg
                    className="h-5 w-5 text-green-500 mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="ml-2">Answer structure refinement</span>
                </li>
                <li className="flex items-start">
                  <svg
                    className="h-5 w-5 text-green-500 mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="ml-2">Knowledge gap elimination</span>
                </li>
              </ul>
            </motion.div>
          </div>

          <div className="mt-16 bg-gray-50 p-8 rounded-xl">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900">
                Why Our Interview Preparation Stands Out
              </h3>
              <p className="mt-2 text-gray-600">
                Our program is designed specifically for NPSC aspirants,
                focusing on Nagaland's unique context
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="flex flex-col items-center text-center p-4">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 mb-4">
                  <svg
                    className="w-8 h-8"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">
                  Expert Panel
                </h4>
                <p className="text-gray-600 text-sm">
                  Former NPSC board members and successful candidates on our
                  coaching team
                </p>
              </div>
              <div className="flex flex-col items-center text-center p-4">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 mb-4">
                  <svg
                    className="w-8 h-8"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                  </svg>
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">
                  Proven Results
                </h4>
                <p className="text-gray-600 text-sm">
                  Over 85% success rate among candidates who complete our
                  program
                </p>
              </div>
              <div className="flex flex-col items-center text-center p-4">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 mb-4">
                  <svg
                    className="w-8 h-8"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                    />
                  </svg>
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">
                  Nagaland Focus
                </h4>
                <p className="text-gray-600 text-sm">
                  Specialized preparation for Nagaland-specific topics and
                  perspectives
                </p>
              </div>
              <div className="flex flex-col items-center text-center p-4">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 mb-4">
                  <svg
                    className="w-8 h-8"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">
                  Continuous Feedback
                </h4>
                <p className="text-gray-600 text-sm">
                  Iterative improvement through multiple practice sessions and
                  feedback loops
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interview Tips Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h2
              className="text-3xl font-extrabold text-gray-900 sm:text-4xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Essential Interview Tips
            </motion.h2>
            <motion.p
              className="mt-4 max-w-2xl text-xl text-gray-600 mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Key strategies to help you excel in your NPSC interview
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {interviewTips.map((tip, index) => (
              <motion.div
                key={tip.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-300"
              >
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 mb-4">
                  {tip.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {tip.title}
                </h3>
                <p className="text-gray-600 mb-4">{tip.description}</p>
                <ul className="space-y-2">
                  {tip.tips.map((item, i) => (
                    <li key={i} className="flex items-start">
                      <svg
                        className="h-5 w-5 text-gray-500 mt-0.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span className="ml-2 text-gray-600 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sample Questions Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h2
              className="text-3xl font-extrabold text-gray-900 sm:text-4xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Sample Interview Questions
            </motion.h2>
            <motion.p
              className="mt-4 max-w-2xl text-xl text-gray-600 mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Prepare with these commonly asked questions in NPSC interviews
            </motion.p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Categories */}
            <div className="lg:col-span-1 bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                Categories
              </h3>
              <ul className="space-y-2">
                {sampleQuestions.map((category, index) => (
                  <li key={index}>
                    <a
                      href={`#category-${index}`}
                      className="flex items-center p-2 text-gray-700 hover:bg-gray-50 hover:text-gray-700 rounded-lg transition-colors duration-300"
                    >
                      <span className="w-6 h-6 flex items-center justify-center bg-gray-100 text-gray-800 rounded-full mr-2 text-xs">
                        {index + 1}
                      </span>
                      <span>{category.category}</span>
                    </a>
                  </li>
                ))}
              </ul>
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-700">
                  These are sample questions to help you prepare. The actual
                  interview questions may vary based on current events and your
                  specific background.
                </p>
              </div>
            </div>

            {/* Questions */}
            <div className="lg:col-span-4">
              <div className="space-y-8">
                {sampleQuestions.map((category, index) => (
                  <div
                    key={index}
                    id={`category-${index}`}
                    className="bg-white p-6 rounded-lg shadow-md"
                  >
                    <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                      <span className="w-8 h-8 flex items-center justify-center bg-gray-100 text-gray-800 rounded-full mr-3 text-sm">
                        {index + 1}
                      </span>
                      {category.category}
                    </h3>
                    <ul className="space-y-4">
                      {category.questions.map((question, qIndex) => (
                        <li key={qIndex} className="flex items-start">
                          <svg
                            className="h-5 w-5 text-gray-500 mt-0.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          <span className="ml-2 text-gray-700">{question}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              <div className="mt-8 text-center">
                <a
                  href="#packages"
                  className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-gray-600 hover:bg-gray-700 transition-colors duration-300"
                >
                  Get Access to Full Question Bank
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Expert Panel Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h2
              className="text-3xl font-extrabold text-gray-900 sm:text-4xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Meet Our Interview Experts
            </motion.h2>
            <motion.p
              className="mt-4 max-w-2xl text-xl text-gray-600 mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Learn from experienced professionals who have been on both sides
              of the interview table
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {expertPanel.map((expert, index) => (
              <motion.div
                key={expert.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-md overflow-hidden"
              >
                <div className="h-52 bg-gray-300">
                  <img
                    src={expert.image || "/images/default-avatar.jpg"}
                    alt={expert.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">
                    {expert.name}
                  </h3>
                  <p className="text-gray-600 text-sm font-medium mb-3">
                    {expert.position}
                  </p>
                  <div className="flex items-center text-sm text-gray-600 mb-2">
                    <svg
                      className="w-4 h-4 mr-1 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                    {expert.experience}
                  </div>
                  <div className="flex items-center text-sm text-gray-600 mb-4">
                    <svg
                      className="w-4 h-4 mr-1 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                      />
                    </svg>
                    {expert.expertise}
                  </div>
                  <button className="w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 text-sm font-medium rounded transition-colors duration-300">
                    View Profile
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section id="packages" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h2
              className="text-3xl font-extrabold text-gray-900 sm:text-4xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Interview Preparation Packages
            </motion.h2>
            <motion.p
              className="mt-4 max-w-2xl text-xl text-gray-600 mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Choose the package that best fits your preparation needs and
              budget
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {interviewPackages.map((pkg, index) => (
              <motion.div
                key={pkg.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`bg-white rounded-lg shadow-lg overflow-hidden border ${
                  pkg.recommended
                    ? "border-gray-500 transform scale-105 z-10"
                    : "border-gray-200"
                } hover:shadow-xl transition-all duration-300`}
              >
                {pkg.recommended && (
                  <div className="bg-gray-600 text-white text-center text-sm font-medium py-1">
                    Recommended
                  </div>
                )}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {pkg.title}
                  </h3>
                  <p className="text-gray-600 mb-6">{pkg.description}</p>

                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">
                      What's Included:
                    </h4>
                    <ul className="space-y-3">
                      {pkg.features.map((feature, i) => (
                        <li key={i} className="flex items-start">
                          <svg
                            className="h-5 w-5 text-green-500 mt-0.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          <span className="ml-2 text-gray-600">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="flex items-center justify-between mb-6">
                    <span className="text-2xl font-bold text-gray-600">
                      {pkg.price}
                    </span>
                    <span className="text-sm text-gray-500">
                      One-time payment
                    </span>
                  </div>

                  <button
                    className={`block w-full text-center py-3 rounded-md font-medium ${
                      pkg.recommended
                        ? "bg-gray-600 text-white hover:bg-gray-700"
                        : "bg-gray-100 text-gray-900 hover:bg-gray-200"
                    } transition-colors duration-300`}
                  >
                    Select Package
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mock Interview Section */}
      <section id="mock-interview" className="py-16 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:flex lg:items-center lg:justify-between">
            <div className="lg:w-1/2 lg:pr-12 mb-8 lg:mb-0">
              <motion.h2
                className="text-3xl font-extrabold tracking-tight mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                Book a Single Mock Interview
              </motion.h2>
              <motion.p
                className="text-lg text-gray-200 mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                Not ready for a full package? Start with a single mock interview
                session to assess your current preparation level and get expert
                feedback.
              </motion.p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <svg
                    className="h-6 w-6 text-gray-300 mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                  </svg>
                  <div className="ml-3">
                    <h3 className="text-lg font-medium text-white">
                      Realistic Interview Simulation
                    </h3>
                    <p className="text-gray-200">
                      Experience a 30-minute mock interview with a panel of
                      experts
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <svg
                    className="h-6 w-6 text-gray-300 mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                  </svg>
                  <div className="ml-3">
                    <h3 className="text-lg font-medium text-white">
                      Detailed Feedback
                    </h3>
                    <p className="text-gray-200">
                      Get a comprehensive evaluation of your performance with
                      actionable tips
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <svg
                    className="h-6 w-6 text-gray-300 mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                  </svg>
                  <div className="ml-3">
                    <h3 className="text-lg font-medium text-white">
                      Video Recording
                    </h3>
                    <p className="text-gray-200">
                      Receive a recording of your session for self-analysis
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-center">
                <span className="text-3xl font-bold text-white">₹2,499</span>
                <span className="ml-2 text-gray-200">per session</span>
              </div>
            </div>
            <div className="lg:w-1/2 bg-white rounded-lg shadow-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Book Your Mock Interview
              </h3>
              <form>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Full Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500"
                      placeholder="Your email"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500"
                      placeholder="Your phone"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="date"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Preferred Date
                    </label>
                    <input
                      type="date"
                      id="date"
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label
                    htmlFor="time"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Preferred Time
                  </label>
                  <select
                    id="time"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500"
                  >
                    <option value="">Select a time slot</option>
                    <option value="10:00 AM">10:00 AM</option>
                    <option value="11:30 AM">11:30 AM</option>
                    <option value="2:00 PM">2:00 PM</option>
                    <option value="3:30 PM">3:30 PM</option>
                    <option value="5:00 PM">5:00 PM</option>
                  </select>
                </div>
                <div className="mb-4">
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Additional Information
                  </label>
                  <textarea
                    id="message"
                    rows={3}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500"
                    placeholder="Tell us about your preparation level and any specific areas you want to focus on"
                  ></textarea>
                </div>
                <div className="mb-4">
                  <div className="flex items-start">
                    <input
                      id="terms"
                      type="checkbox"
                      className="h-4 w-4 text-gray-600 focus:ring-gray-500 border-gray-300 rounded mt-1"
                    />
                    <label
                      htmlFor="terms"
                      className="ml-2 block text-sm text-gray-700"
                    >
                      I agree to the{" "}
                      <a href="#" className="text-gray-600 hover:text-gray-700">
                        terms and conditions
                      </a>{" "}
                      and{" "}
                      <a href="#" className="text-gray-600 hover:text-gray-700">
                        privacy policy
                      </a>
                    </label>
                  </div>
                </div>
                <button
                  type="submit"
                  className="w-full py-3 px-4 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-md shadow-sm"
                >
                  Book Now & Pay ₹2,499
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h2
              className="text-3xl font-extrabold text-gray-900 sm:text-4xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Success Stories
            </motion.h2>
            <motion.p
              className="mt-4 max-w-2xl text-xl text-gray-600 mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Hear from candidates who aced their NPSC interviews with our
              guidance
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gray-50 p-6 rounded-lg border border-gray-200 shadow-md relative"
              >
                <svg
                  className="w-12 h-12 text-gray-100 absolute top-4 left-4"
                  fill="currentColor"
                  viewBox="0 0 32 32"
                >
                  <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
                </svg>
                <p className="italic text-gray-600 mb-6 pt-6">
                  {testimonial.content}
                </p>
                <div className="flex items-center">
                  <div className="mr-4 h-12 w-12 rounded-full overflow-hidden bg-gray-300">
                    <img
                      src={testimonial.image}
                      alt={testimonial.author}
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-gray-900">
                      {testimonial.author}
                    </h3>
                    <p className="text-gray-600 font-medium">
                      {testimonial.position}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h2
              className="text-3xl font-extrabold text-gray-900 sm:text-4xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Frequently Asked Questions
            </motion.h2>
            <motion.p
              className="mt-4 max-w-2xl text-xl text-gray-600 mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Find answers to common questions about our interview preparation
              program
            </motion.p>
          </div>

          <div className="max-w-3xl mx-auto">
            {faqs.map((faq, index) => (
              <div key={index} className="mb-4">
                <button
                  className="flex justify-between items-center w-full p-4 bg-white rounded-lg shadow-md focus:outline-none"
                  onClick={() => toggleFaq(index)}
                >
                  <h3 className="text-lg font-medium text-gray-900 text-left">
                    {faq.question}
                  </h3>
                  <svg
                    className={`w-5 h-5 text-gray-500 transform ${
                      selectedFaq === index ? "rotate-180" : ""
                    } transition-transform duration-300`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                {selectedFaq === index && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    transition={{ duration: 0.3 }}
                    className="p-4 bg-white rounded-b-lg shadow-md mt-1"
                  >
                    <p className="text-gray-600">{faq.answer}</p>
                  </motion.div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-gray-900 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">
            Ready to Ace Your NPSC Interview?
          </h2>
          <p className="text-xl text-gray-200 mb-8 max-w-3xl mx-auto">
            Don't leave your final NPSC hurdle to chance. Prepare systematically
            with experts who understand what it takes to succeed.
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <a
              href="#packages"
              className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-gray-900 bg-yellow-400 hover:bg-yellow-500 transition-colors duration-300 shadow-md"
            >
              View Packages
            </a>
            <a
              href="#mock-interview"
              className="inline-flex items-center justify-center px-6 py-3 border-2 border-white text-base font-medium rounded-md text-white hover:bg-white/10 transition-colors duration-300"
            >
              Book Mock Interview
            </a>
          </div>
        </div>
      </section>

      {/* Final Section with Stats */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gray-50 rounded-2xl p-8 md:p-12">
            <div className="text-center mb-10">
              <h2 className="text-2xl font-bold text-gray-900">
                Why Choose Our Interview Preparation Program?
              </h2>
              <p className="mt-2 text-lg text-gray-600">
                Our track record speaks for itself
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <div className="text-3xl font-bold text-gray-600 mb-2">
                  85%+
                </div>
                <p className="text-gray-700">
                  Success rate among our interview program participants
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <div className="text-3xl font-bold text-gray-600 mb-2">
                  500+
                </div>
                <p className="text-gray-700">
                  Successful NPSC candidates mentored
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <div className="text-3xl font-bold text-gray-600 mb-2">12+</div>
                <p className="text-gray-700">
                  Years of interview coaching experience
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <div className="text-3xl font-bold text-gray-600 mb-2">
                  4.9/5
                </div>
                <p className="text-gray-700">
                  Average participant satisfaction rating
                </p>
              </div>
            </div>

            <div className="mt-10 text-center">
              <p className="text-gray-600 mb-4">
                Contact us for any questions or custom interview preparation
                needs
              </p>
              <a
                href="/contact"
                className="inline-flex items-center justify-center px-5 py-2 border border-transparent text-base font-medium rounded-md text-white bg-gray-600 hover:bg-gray-700 transition-colors duration-300"
              >
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InterviewPrepPage;
